import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Facebook } from "lucide-react";
import { API, LOGO_LIGHT, CONTACT_INFO } from "@/lib/constants";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed", {
        description: "You're on the list. À bientôt.",
      });
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe", {
        description: "Please check the email and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="footer-container"
      className="bg-[#EAE5D9] text-[#1A1713] pt-24 md:pt-32 pb-12"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-12 pb-16 border-b border-[#1A1713]/15">
          {/* Logo and tag */}
          <div className="col-span-12 md:col-span-5">
            <img
              src={LOGO_LIGHT}
              alt="Ladiosa's"
              className="h-32 md:h-40 w-auto object-contain -ml-4"
            />
            <p className="font-serif-display italic text-2xl md:text-3xl mt-8 max-w-sm leading-snug">
              A Houston brasserie that cooks like its grandmothers — Soul,
              Spanish & Mediterranean.
            </p>
          </div>

          {/* Visit */}
          <div className="col-span-6 md:col-span-3">
            <p className="font-mono-accent text-[12px] text-[#7A2021] mb-5">
              Visit
            </p>
            <p>{CONTACT_INFO.street}</p>
            <p className="text-[#5C5449]">{CONTACT_INFO.cityState}</p>
            <p className="mt-4">{CONTACT_INFO.phone}</p>
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="text-[#5C5449] brass-link inline-block"
            >
              {CONTACT_INFO.email}
            </a>
            <a
              href={CONTACT_INFO.ig_url}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-ig-link"
              className="mt-6 inline-flex items-center gap-2 text-[#1A1713] brass-link"
            >
              <Instagram size={16} />
              {CONTACT_INFO.ig}
            </a>
            <a
              href={CONTACT_INFO.fb_url}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-fb-link"
              className="mt-3 inline-flex items-center gap-2 text-[#1A1713] brass-link"
            >
              <Facebook size={16} />
              {CONTACT_INFO.fb}
            </a>
          </div>

          {/* Newsletter */}
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-accent text-[12px] text-[#7A2021] mb-5">
              The House Bulletin
            </p>
            <p className="text-[#5C5449] mb-5 leading-relaxed">
              New dishes, brunch dates, and the occasional secret menu —
              monthly, never spammy.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="flex border-b border-[#1A1713]/30">
              <input
                type="email"
                required
                placeholder="email@address.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="flex-1 bg-transparent py-3 outline-none text-[#1A1713] placeholder:text-[#5C5449]/60"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="font-mono-accent text-[12px] text-[#7A2021] px-4 disabled:opacity-50"
              >
                {busy ? "…" : "Join"}
              </button>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:justify-between gap-3 text-[#5C5449] text-sm">
          <p className="font-mono-accent text-[12px]">
            © {new Date().getFullYear()} Ladiosa's Homestyle Kitchen · Houston, TX
          </p>
          <p className="font-mono-accent text-[12px]">
            Designed with butter, brass & bossa nova
          </p>
        </div>
      </div>
    </footer>
  );
}
